/**
 * Reloading onto the build that is actually deployed.
 *
 * A plain reload is not enough. The service worker answers navigations from the
 * precached `index.html`, so navigating again returns the same stale shell — the
 * new worker sits in `waiting` and, by the browser's own rule, only takes over
 * once every tab for the origin has closed. `src/service-worker.ts` already
 * listens for a `SKIP_WAITING` message; until this module existed, nothing sent
 * one, so the handler had no caller and the reload prompt could not deliver what
 * it promised.
 */

/** The message `src/service-worker.ts` listens for to activate immediately. */
const SKIP_WAITING_MESSAGE = { type: "SKIP_WAITING" };

/**
 * How long to wait for the new worker to take control before reloading anyway.
 *
 * The reload still helps when the worker is slow or the swap never lands — the
 * prompt was clicked, so the visitor is owed a page, not a spinner.
 */
const CONTROLLER_CHANGE_TIMEOUT_MS = 3_000;

/**
 * How long to let a worker finish installing before giving up on it.
 *
 * Installing means precaching the whole app shell, which is hundreds of files —
 * slower than the update check that discovered it, and the reason the worker is
 * rarely already waiting by the time we look.
 */
const INSTALL_TIMEOUT_MS = 10_000;

/**
 * The worker that is ready to take over, waiting for it to finish installing.
 *
 * `registration.update()` resolves when the *check* completes, not when the
 * worker it found is ready — so reading `registration.waiting` straight after it
 * usually finds nothing, and treating that as "nothing to activate" sends the
 * visitor back to the same precached shell. A worker still installing becomes
 * the waiting one the moment it reaches `installed`.
 *
 * @param registration - The active service worker registration
 * @returns The worker to activate, or null when there is none
 */
const resolveWorkerAwaitingActivation = (
  registration: ServiceWorkerRegistration
): Promise<ServiceWorker | null> => {
  if (registration.waiting) {
    return Promise.resolve(registration.waiting);
  }

  const installing: ServiceWorker | null = registration.installing;
  if (installing === null) {
    return Promise.resolve(null);
  }
  // Held in its own binding so the narrowing survives into the listener below.
  const pending: ServiceWorker = installing;

  return new Promise<ServiceWorker | null>((resolve) => {
    let settled = false;
    const settle = (worker: ServiceWorker | null): void => {
      if (settled) {
        return;
      }
      settled = true;
      pending.removeEventListener("statechange", onStateChange);
      resolve(worker);
    };

    function onStateChange(): void {
      if (pending.state === "installed") {
        settle(registration.waiting ?? pending);
      } else if (pending.state === "activated" || pending.state === "redundant") {
        // Activated without waiting (nothing was controlling) or discarded —
        // either way there is no waiting worker to message.
        settle(null);
      }
    }

    pending.addEventListener("statechange", onStateChange);
    window.setTimeout(() => settle(registration.waiting), INSTALL_TIMEOUT_MS);
  });
};

/**
 * Activate the waiting service worker, then reload onto the build it serves.
 *
 * Falls back to a plain reload whenever there is nothing to activate: no service
 * worker support, no registration, or no worker waiting. Never rejects — every
 * path ends in a reload, because a prompt the visitor clicked must not leave the
 * page sitting where it was.
 *
 * @returns Promise resolving once a reload has been requested
 */
export const reloadWithLatestServiceWorker = async (): Promise<void> => {
  const reload = (): void => {
    window.location.reload();
  };

  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    reload();
    return;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      reload();
      return;
    }

    // Ask for the newest worker first: the visitor may have clicked the prompt
    // before the background update check had fetched it.
    await registration.update().catch(() => undefined);

    const waiting = await resolveWorkerAwaitingActivation(registration);
    if (!waiting) {
      reload();
      return;
    }

    let hasReloaded = false;
    const reloadOnce = (): void => {
      if (hasReloaded) {
        return;
      }
      hasReloaded = true;
      reload();
    };

    navigator.serviceWorker.addEventListener("controllerchange", reloadOnce, { once: true });
    window.setTimeout(reloadOnce, CONTROLLER_CHANGE_TIMEOUT_MS);

    waiting.postMessage(SKIP_WAITING_MESSAGE);
  } catch {
    // A registration lookup can throw in a private window or with storage
    // blocked. The visitor still asked for the new version.
    reload();
  }
};
